module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('p', {
    staticClass: "h2a"
  }, [_c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("CHAPTER 3")])]), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_c('p', {
    staticClass: "h2"
  }, [_vm._v("Territory Shaped")]), _vm._v(" "), _c('p', {
    staticClass: "extract"
  }, [_vm._v("To the north of Nuevo Santander is found the province of Texas, but one must cross a great desert or remote lands between the last populations of this colony and the first settlements of that one.")]), _vm._v(" "), _c('p', {
    staticClass: "right"
  }, [_vm._v("—JOSÉ TIENDA DE CUERVO, 1757")])]), _c('p', {
    staticClass: "noindentt"
  }, [_c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("For most of the three centuries of Spanish colonial rule, northeastern Mexico and southern Texas were poorly defined parts of the north of New Spain.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn1",
      "id": "ch3_rfn1",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("1")])]), _vm._v(" This realm today includes chiefly the southern reaches of Texas in the United States and the Mexican states of Coahuila, Nuevo León, and Tamaulipas (colonial Nuevo Santander). Yet, its contemporary political geography disguises the considerable uncertainties about boundaries that existed for much of this domain, especially the Rio Grande and Nueces River frontiers. These boundaries were shaped through individual ambition, imperial rivalry, and civil and international conflict."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn2",
      "id": "ch3_rfn2",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("2")])]), _vm._v(" South Texas is the most recent cultural-political configuration in this framework of changing regional geography.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In this chapter I begin by outlining the political geographic history of this region. A considerable emphasis is placed on the question "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("of shifting boundaries, particularly among Spanish Texas, Coahuila, and Nuevo Santander. This reconstruction is essential to understanding how and why South Texas is a distinctive subcultural area and one rooted in New Spain (Mexico).")])]), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("Defining territory by political demarcation and settling the same area are, however, two separate processes. Spanish colonial officials were remarkably bold in expressing claims to parts of New Spain, even if their maps were imprecise about the exact outlines of that grand area. Colonizing these same frontiers was not a simple exercise. To be sure, mineral riches and slave raiding inspired individuals to stake their claims to parts of the Mexican north, but forging a community and sustaining settled life in this distant land, perceived as wilderness, amidst often combative aboriginals and a harsh environment were a constant challenge.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn3",
      "id": "ch3_rfn3",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("3")])]), _vm._v(" It is ironic and yet instructive to recall that in these frontier provinces the estimated population settled at the close of the colonial period in 1821 was less than the total number of native inhabitants in the respective districts at the beginning of Spanish reduction in 1519."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn4",
      "id": "ch3_rfn4",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("4")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Largely, Spanish success was measured by the dozens of towns and ranches founded in this region, many of which are still on the map. The second purpose of this chapter, then, will be to assess the historical geography of early settlement. The concern is to understand where, when, and how these footholds were organized and founded, and how they survived to become the system of nodes that would give human geographic structure to the region in the twentieth century.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("When the Treaty of Guadalupe Hidalgo was signed in 1848, Mexicans and other residents of Spanish heritage in South Texas were confronted with a new formal political allegiance. In less than five decades, what had been for several centuries chiefly a thinly populated Spanish-colonial frontier was transformed by non-Hispanic colonization in the nineteenth and early twentieth centuries into a Mexican American frontier. The number of viable communities established increasingly included Anglo Americans and exceeded many times the framework of Spanish settlements. South Texas persisted nevertheless as a Mexican American cultural zone through the end of the nineteenth century, and this chapter closes with a broad sketch of that human geography.")]), _vm._v(" "), _c('p', {
    staticClass: "h3"
  }, [_vm._v("BOUNDARIES")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("The northern frontier of New Spain evolved as a set of "), _c('i', [_vm._v("gobiernos")]), _vm._v(" or administrative "), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("units, each ruled by a governor who reported to the viceroy in Mexico City during the colonial era from 1519–1775. In the northeast, five separate jurisdictions emerged: Pánuco, Nuevo León, Coahuila, Texas, and Nuevo Santander. In the later years of the colonial era, beginning in 1776, administrative control of the north passed to a series of military divisions headed by a "), _c('i', [_vm._v("comandante general")]), _vm._v(" or commander-general. These "), _c('i', [_vm._v("provincias internas")]), _vm._v(" or interior provinces were reduced in 1787 to Occidente (western) and Oriente (eastern) commands; the latter included Coahuila, Nuevo Leon, Texas, and Nuevo Santander. Because authority among the viceroy, governors, and commanders-general shifted back and forth during the later years of Spanish colonial administration, jurisdictional disputes over territory were common and exact political boundaries were not always clear. Ecclesiastical and missionary administrative units that were separate from these civil and military organizations did not directly impact political boundaries and therefore will not be considered in this discussion.")])]), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("Northeastern New Spain was first given formal administrative authority as the "), _c('i', [_vm._v("gobierno")]), _vm._v(" of San Esteban de Pánuco in 1523. Its assumed boundaries were the Río Tuxpan on the south and the Río Pánuco on the north; it extended from the Gulf coast inland to the Sierra Madre Oriental.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn5",
      "id": "ch3_rfn5",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("5")])]), _vm._v(" Presumably, its jurisdiction stretched north into what would become Nuevo Santander, but the boundary was ill defined at this time. This was largely the region known as the Huasteca, the homeland of an aboriginal agricultural people who occupied the north-easternmost reach of pre-Columbian high culture in Mesoamerica."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn6",
      "id": "ch3_rfn6",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("6")])]), _vm._v(" The Pánuco was a notorious slaving area ravaged for its Native American labor by Nuño Beltrán de Guzmán. When Guzmán transferred to Nueva Galicia, the Pánuco was absorbed as a political unit by the "), _c('i', [_vm._v("gobierno")]), _vm._v(" of Nueva España in 1534. The Spanish interest in the region waned for nearly fifty years, until 1580 when Nuevo León was organized.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Nuevo León was an ambitious territorial design that initially claimed as its domain much of northeastern Mexico east of the Sierra Madre Oriental to the Gulf of Mexico."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn7",
      "id": "ch3_rfn7",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("7")])]), _vm._v(" The administrative boundaries were finally compressed to the approximate outlines of the contemporary state of the same name ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig1"
    }
  }, [_vm._v("FIG. 3.1")]), _vm._v("). The limestone massif of the Sierra Madre Oriental is the western backbone of the province, and this mountain range stretches southeast beyond the boundary with Nuevo Santander (present Tamaulipas). Its northeastern periphery is coastal plain spread between the Ríos Salado and San Juan that drains to the Rio Grande, while its southwestern edge pressed against the borders of Nueva Galicia "), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("and Nueva Vizcaya (present-day San Luis Potosí) that are part of the Mesa del Norte.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn8",
      "id": "ch3_rfn8",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("8")])]), _vm._v(" In the colonial era, Nuevo León was a mining and ranching frontier within a mixed aboriginal realm that included hunting and gathering groups like the Bocalo and Coahuiltecan, as well as others. The near constant trafficking in Native American slaves resulted in long periods of conflict, and created a vacuum that led to the importation of native groups from the south and later incursions by Apache from north of the Rio Grande."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn9",
      "id": "ch3_rfn9",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("9")])])]), _vm._v(" "), _c('p', {
    staticClass: "image",
    attrs: {
      "id": "ch3fig1"
    }
  }, [_c('img', {
    staticStyle: {
      "width": "79%"
    },
    attrs: {
      "alt": "image",
      "src": "html/images/image26_00.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("3.1. Colonial administrative boundaries of Northeast New Spain and South Texas.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("Coahuila, also known as Nueva Extremadura, a region penetrated in the sixteenth century and claimed by rival provinces—Nuevo León to its east and Nueva Vizcaya to its south and west—was a vaguely defined territory even after it was declared a separate "), _c('i', [_vm._v("gobierno")]), _vm._v(" in 1687. On its northern edge, its borders were roughly the Medina River, west of San Antonio, and a line that reached from that boundary south until it intersected the Rio Grande northwest of Laredo (")]), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig1"
    }
  }, [_vm._v("FIG. 3.1")]), _vm._v("). To the northwest, the territory was poorly defined but was presumed to include the San Sabá settlements on the Edwards Plateau and to extend northwest until Nuevo México. On the southwest, the province bordered Nueva Vizcaya, and in 1787 its southern flank was expanded to include the towns of Saltillo and Parras."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn10",
      "id": "ch3_rfn10",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("10")])]), _vm._v(" A series of folded ranges trending northwest to southeast divides Coahuila into a northeastern coastal plain and a southwestern upland that is part of the Mesa del Norte."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn11",
      "id": "ch3_rfn11",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("11")])]), _vm._v(" The Río Sabinas drains the region southeast to the border with Nuevo León, where the stream feeds the Río Salado. The coastal plain lands were the western edge of the greater Coahuiltecan realm, while the more arid interior west of the Sierra was only sparsely populated with native groups. While missionaries attempted to settle and organize some of the Coahuiltecan populations, the settlements like Saltillo that were populated with Tlaxcaltecans from the south were more successful."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn12",
      "id": "ch3_rfn12",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("12")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Although explored and even partly settled in the late seventeenth century, Texas (Tejas) was politically an extension of Coahuila until 1722. Officially, the province was known as Nuevo Reino de las Filipinas, or Nuevas Filipinas."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn13",
      "id": "ch3_rfn13",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("13")])]), _vm._v(" Besides the arc of the Gulf coast on the east, jurisdictional boundaries were said to be the Medina-San Antonio Rivers on the south and the Red River on the north, but neither limit was firm. On the south, an unsettled zone stretched across the trans-Nueces frontier to the Rio Grande until this area began to be colonized as Nuevo Santander in the mid-eighteenth century. To the north, the divide between New Spain and New France was vague until the Adams-Onís Treaty in the early nineteenth century. Toward the interior and above the Balcones Escarpment, borders were uncertain because settlement did not much penetrate beyond the pivot points of San Antonio or Los Adaes (near present-day Robeline, Louisiana), each located on the coastal plain."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn14",
      "id": "ch3_rfn14",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("14")])]), _vm._v(" This condition led historian Herbert Eugene Bolton to proclaim Texas “a buffer province” during the critical period of the eighteenth century separating northern New Spain from French territory in the Mississippi River drainage."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn15",
      "id": "ch3_rfn15",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("15")])]), _vm._v(" Aboriginal populations in the southern reaches of the "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("province consisted primarily of Coahuiltecan hunter-gatherers both in the interior and along the coastal littoral.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn16",
      "id": "ch3_rfn16",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The last "), _c('i', [_vm._v("gobierno")]), _vm._v(" created in the northeast of New Spain was Nuevo Santander in 1748. This province was bordered on the south by Nueva España and on the west by Nuevo León. Each of these areas had been settled earlier, so claims to territory were especially sensitive, although they were ultimately resolved in favor of Nuevo Santander."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn17",
      "id": "ch3_rfn17",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("17")])]), _vm._v(" Nevertheless, it was chiefly on the north—a "), _c('i', [_vm._v("despoblado")]), _vm._v(" or unpopulated zone where Nuevo Santander abutted Texas and Coahuila—that boundary questions persisted late into the colonial period. Topographically, Nuevo Santander is chiefly a coastal plain that stretches for approximately six degrees of latitude from near the Río Pánuco on the south to the San Antonio River on the north, and is roughly divided by the Rio Grande ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig1"
    }
  }, [_vm._v("FIG. 3.1")]), _vm._v("). On the south, rivers like the Purificación—Soto La Marina and Conchas–San Fernando drain eastward from the Sierra Madre, while streams like the Nueces on the north flow easterly from the Balcones Escarpment. These rivers empty into the Gulf of Mexico via numerous estuaries and bays of the barrier-island coastline. The only significant uplands in the province are the isolated Sierras de San Carlos and de Tamaulipas in the southern portion of the area below the Rio Grande. While the region was said to be part of the Coahuiltecan domain, recent studies suggest that the diversity of groups in this zone may have been significantly more complex than earlier assessments realized."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn18",
      "id": "ch3_rfn18",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("18")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Of particular concern to an understanding of the historical political geography of this province is the issue of the changing assessment of its northern boundary with Texas. In 1721, the Marqués de Aguayo, governor of Coahuila, Nueva Extremadura, Texas, and Nuevas Filipinas, acknowledged that the Medina River was the boundary between Texas and Coahuila, and the Nueces River the divide between Texas and Nuevo Santander."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn19",
      "id": "ch3_rfn19",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("19")])]), _vm._v(" One the earliest detailed maps of what would become Nuevo Santander is attributed to Ladrón de Guevara in 1736."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn20",
      "id": "ch3_rfn20",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("20")])]), _vm._v(" This map shows the coastal lands between the Río Pánuco and the Rio Bravo (Rio Grande) as well as lands north into the province of Texas, indicated as lying north of a mountain range situated well above the Río Bravo. Across the horn of present South Texas and extending as far south as the Río Pánuco is scrawled “País despoblado hasta la costa” (Uninhabited land as far as the coast). When José de Escandón explored these lands in 1747 in preparation for the colonization of Nuevo Santander, he added significantly to Guevara’s map by indicating that, in fact, the coastal zone was not “despoblado” "), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }, [_vm._v("but included various aboriginal groups. Importantly, Escandón’s "), _c('i', [_vm._v("Mapa de la Sierra Gorda y costa de el Seno Mexicano")]), _vm._v(" also designated jurisdictional boundaries for neighboring "), _c('i', [_vm._v("gobiernos")]), _vm._v(". ")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn21",
      "id": "ch3_rfn21",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("21")])]), _vm._v(" The lands north of the Río Nueces are clearly assigned as "), _c('i', [_vm._v("“Parte de la jurisdicción de Texas,”")]), _vm._v(" suggesting in no uncertain terms that Escandón believed this to be the boundary between Texas and what would become Nuevo Santander.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In 1757 José Tienda de Cuervo conducted an inspection of the Mexican north. His report was unequivocal about the northern boundary of Nuevo Santander being the southern reach of the province of Texas, but he was uncertain about the frontier between Texas and the Rio Grande because he had not traveled that territory (see the epigraph at the introduction to this chapter). Agustín López de la Cámara Alta, official engineer, signed the principal map that resulted from the inspection in 1758. This map and others made from it, like the one drafted by Francisco José de Haro, show the northern boundary of Nuevo Santander as the San Antonio River, not the Nueces River that had earlier appeared on Escandón’s 1747 map."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn22",
      "id": "ch3_rfn22",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("22")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Further confusion arises from the 1771 map that resulted from the "), _c('i', [_vm._v("relatión")]), _vm._v(" or report produced by Nicolás de La Fora as part of the 1766 brief inspection of the northern frontier by the Marqués de Rubí."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn23",
      "id": "ch3_rfn23",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("23")])]), _vm._v(" La Fora’s account and map suggest that the boundary between Nuevo Santander and Texas is the Nueces River, not the San Antonio River, although the Nueces is not formally labeled on his map. His map shows Nuevo Santander below a line that appears to be the Nueces. In the text of his report, however, he notes that the colony of Nuevo Santander “extends from Laredo following the course of the Río Grande del Norte to the Gulf of Mexico, as shown on the general map” and thus further confuses the boundary issue."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn24",
      "id": "ch3_rfn24",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In 1804, the great German geographer Alexander von Humboldt published his highly regarded "), _c('i', [_vm._v("Political Essay on the Kingdom of New Spain")]), _vm._v(" in several volumes, including an atlas. A single map in this atlas is titled “Carta general del reino de la Nueva España.” It shows the boundary between Texas and Nuevo Santander as a line that starts near the Mission Espada (south of San Antonio Valero, the Alamo) and then intersects the Nueces River near 28° north latitude and then follows the river along its south bank to its mouth. Humboldt did not survey this terrain firsthand but relied on materials available to him in the archives in Mexico City. In the introduction to his "), _c('i', [_vm._v("Political Essay")]), _vm._v(", Humboldt admits that he was unable to uncover sufficient data in the Mexican archives to compose the Texas portion "), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }, [_vm._v("of the Mexico map. Instead, he drew the Texas map based upon information provided by U.S. General James Wilkinson, commander of United States forces in New Orleans, whom he met in Washington upon his return from Mexico.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn25",
      "id": "ch3_rfn25",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("25")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("An official decree in 1805 to the "), _c('i', [_vm._v("comandante general")]), _vm._v(" of the Provincias Internas de Oriente, Joaquín de Arredondo, resulted in the drafting of a map to show the boundaries of this province that includes Coahuila, Nuevo León, Texas, and Nuevo Santander. This map, published in 1815, shows plainly that the boundary between Texas and Nuevo Santander is the middle and lower Nueces River, starting at between 28° north latitude and 101° west longitude and running southeast toward the mouth of the river."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn26",
      "id": "ch3_rfn26",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("26")])]), _vm._v(" Félix Calleja, then the viceroy of New Spain, approved the map in October of the same year. When Mexico rebelled against Spain and declared itself independent in 1821, the Nueces River became the official divide between the state of Coahuila y Tejas and Tamaulipas, formerly Nuevo Santander.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By the 1830s, Texas geography, as viewed from the north, had become a matter of some speculation. Stephen F. Austin’s early maps became the basis for several commercial maps produced during this period. The least derivative and reputed to be the best of these was that produced by David Burr, geographer to the U.S. House of Representatives. Burr’s map of 1833 was published by J. H. Colton of New York and was quickly imitated by others like J. H. Young, who published in 1837 "), _c('i', [_vm._v("New Map of Texas with Contiguous American and Mexican States")]), _vm._v("."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn27",
      "id": "ch3_rfn27",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("27")])]), _vm._v(" Like the Burr map before it, Young’s map shows that the middle and lower Nueces is the boundary between Texas and Tamaulipas.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("After Texas gained independence from Mexico in 1836, the lands south of the Nueces and north of the Rio Grande were disputed. Texas claimed this territory by right of the Treaty of Velasco that was signed by Santa Anna after his defeat at the Battle of San Jacinto, but Mexico refused to concede. Mexico held that the territory had been part of neither Spanish nor Mexican Texas, because the area encompassed first Nuevo Santander, then Tamaulipas—never Texas."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn28",
      "id": "ch3_rfn28",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("28")])]), _vm._v(" Furthermore, Mexico insisted that the provisions of the Treaty of Velasco that relinquished the area to Texas were null and void because they were unacknowledged by the Mexican Congress. This boundary discord persisted until the United States’ annexation of Texas, which precipitated the U.S.-Mexican War and ended in the Treaty of Guadalupe Hidalgo setting the boundary as the Rio Grande in 1848.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_31"
    }
  }, [_vm._v("The old and new boundaries are illustrated on the map of Tamaulipas published by Antonio García Cubas in 1858 as part of his monumental "), _c('i', [_vm._v("Atlas geográfico, estadístico e histórico de la República Mexicana")]), _vm._v(".")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn29",
      "id": "ch3_rfn29",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("29")])]), _vm._v(" This map shows the Nueces River as the “Antiguo limite de Tamaulipas,” starting northwest of Laredo at 28°30' north latitude and 100°29' west longitude and running southeast to Corpus Christi Bay ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig2"
    }
  }, [_vm._v("FIG. 3.2")]), _vm._v("). The 1858 boundary is the Río Bravo (Rio Grande) northwest of Laredo, at 27°40' north latitude and running southeast to the Boca del Río Bravo at the southern tip of Padre Island. The boundary is labeled “Límite actual entre la República y los Estados Unidos con arreglo al tratado de Guadalupe en 1848.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By the mid-nineteenth century, it was clear that the boundary dispute between Texas and Tamaulipas had finally been resolved through international agreement. However, an academic footnote to this story appeared in 1915, when the eminent borderland historian Herbert Bolton cited the La Fora map as the source for his map, which designated the Nueces as the boundary between Texas and Nuevo Santander."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn30",
      "id": "ch3_rfn30",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("30")])]), _vm._v(" Bolton, however, drew his boundary along the Nueces, but not to its mouth at present Corpus Christi Bay as previous maps from Escandón to Garcia Cubas had done. Rather, Bolton’s map shows the boundary to veer southeast from the Nueces toward the Aransas River (close to 30° north latitude) and then follow that drainage to Aransas Bay. Why this would be so is not clear, because La Fora’s map does not show this level of detail, nor does it label the Aransas River or Bay. Bolton claims that the La Fora map he inspected was housed in the Archivo General de Mexico. Assuming, for the moment, that it is unlikely that Bolton misreported from an archival map, one possible explanation is simply that there exist various copies of the same map. In fact, the distinguished Mexican historian Vito Alessio Robles, who published his version of the La Fora account from a copy in the Ministry of War in Madrid, admits that there are several separate accounts of the document known to exist in Spain, Mexico, and the United States.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The changing perception of boundaries between colonial Nuevo Santander (later Mexican Tamaulipas) and Texas are summarized in "), _c('a', {
    attrs: {
      "href": "#/html/#ch3tab1"
    }
  }, [_vm._v("Table 3.1.")]), _vm._v(" What is clear from the documents is that the region generally south of the Nueces River was, from the mid-eighteenth century, a separate administrative unit of colonial New Spain and later the Republic of Mexico, despite claims to the contrary by the Republic of Texas. In 1848, the issue became resolved through political expediency following the U.S.-Mexico War and the United States annexation of Texas, which had been initiated in 1845. These early struggles would define the southern horn-shape outline of South Texas and give temporary alignment along a northern fringe of the Nueces River. The precise dimensions of Mexican and United States South Texas, however, require consideration of human settlement.")]), _vm._v(" "), _c('p', {
    staticClass: "image",
    attrs: {
      "id": "ch3fig2"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_c('img', {
    staticStyle: {
      "width": "48%"
    },
    attrs: {
      "alt": "image",
      "src": "html/images/image32_00.jpg"
    }
  })])]), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_c('p', {
    staticClass: "caption"
  }, [_vm._v("3.2. Tamaulipas boundary along the Nueces River.")])]), _c('p', {
    staticClass: "tab-cap",
    attrs: {
      "id": "ch3tab1"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_32"
    }
  }), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_c('b', [_vm._v("TABLE 3.1")]), _vm._v("   Changing Definitions of the Nuevo Santander–Texas Boundary")])]), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_c('p', {
    staticClass: "image1"
  }, [_c('img', {
    attrs: {
      "alt": "image",
      "src": "html/images/image33_00.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "h3"
  }, [_vm._v("COLONIZATION")])]), _c('p', {
    staticClass: "noindent"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("Spanish colonial settlement of northeast New Spain, including what would become South Texas, extended over nearly two centuries. The first phase of this protracted experience resulted in the establishment of several foundation towns, both mining and livestock centers, west and south of the present Rio Grande; these include Saltillo (1577), Cerralvo (1583, 1625), Monterrey (1596), Cadereyta (1637), and Monclova (1689).")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn31",
      "id": "ch3_rfn31",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("31")])]), _vm._v(" These settlements were colonized via Durango to the south ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig3"
    }
  }, [_vm._v("FIG. 3.3")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The colonization of Texas occurred during the first half of the eighteenth century, launched chiefly from Saltillo and Monclova, the principal dispersal centers."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn32",
      "id": "ch3_rfn32",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("32")])]), _vm._v(" The strategic pivot point linking Coahuila and Texas was San Juan Bautista (1699–1700), near present-day Villa Guerrero and Eagle Pass and proximate to a ford on the Rio Grande."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn33",
      "id": "ch3_rfn33",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("33")])]), _vm._v(" Later settlements were established at San Antonio de Bexar (1718–1731) on a springfed river below the Balcones Escarpment and at La Bahía del Espíritu Santo (1726–1749) on the San Antonio River along the lower coastal plain."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn34",
      "id": "ch3_rfn34",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("34")])]), _vm._v(" These early nodes were mission and presidio settlements involving ecclesiastical orders and spare military garrisons. San Antonio, with five missions, a presidio, and a civil settlement or "), _c('i', [_vm._v("villa")]), _vm._v(" by 1731, was a major center of the frontier, and that concentration of varied institutions at one general location made the settlement unique in the Spanish borderlands. The "), _c('i', [_vm._v("villa")]), _vm._v(" was known as San Fernando, but the presidio name, San Antonio de Béxar, usually shortened to Béxar, persisted throughout the Spanish period. San Antonio became the provincial capital of Spanish Texas in 1772 and was elevated to city status in 1811, making it the political equal of Saltillo and Monterrey."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn35",
      "id": "ch3_rfn35",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("35")])])]), _vm._v(" "), _c('p', {
    staticClass: "image",
    attrs: {
      "id": "ch3fig3"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_c('img', {
    staticStyle: {
      "width": "65%"
    },
    attrs: {
      "alt": "image",
      "src": "html/images/image34_00.jpg"
    }
  })])]), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_c('p', {
    staticClass: "caption"
  }, [_vm._v("3.3. Colonial settlement of Northeast New Spain and South Texas, with founding dates.")])]), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_34"
    }
  }), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("An early trail connected San Juan Bautista and San Antonio, and this evolved into a major conduit of travel between colonial Coahuila and Texas. This road, probably several separate paths, traversed the northwest of South Texas (see ")]), _c('a', {
    attrs: {
      "href": "#/html/9780292705111_ch2"
    }
  }, [_vm._v("Chap. 2")]), _vm._v(") through present-day Maverick, Dimmit, Zavala, La Salle, Frio, Medina, Atascosa, and Bexar Counties."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn36",
      "id": "ch3_rfn36",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("36")])]), _vm._v(" This "), _c('i', [_vm._v("camino")]), _vm._v(", as it would come to be called, extended west and south to Monclova and Saltillo and east to the Spanish outpost of Los Adaes in present Louisiana. A shorter spur road south from San Antonio linked that settlement to La Bahía and Copano Bay on the coast.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Nuevo Santander was a later colonization effort, contracted to José de Escandón and steered initially from Querétaro through Monterrey."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn37",
      "id": "ch3_rfn37",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("37")])]), _vm._v(" Santander (1749; present-day Santander Jiménez), situated in central Tamaulipas, became the mother settlement for several towns in its vicinity, including Soto la Marina (1750) and Burgos (1749). The settlements along the Rio Grande were established between 1749–1796, and are linked to mother settlements in Nuevo León and Coahuila. The first of these were Camargo (1749), settled from Cerralvo, and Reynosa (1749), mothered by Cadereyta. Mier (1753) too was founded by settlers from Cerralvo, a town that rightly deserves its motto, “Cuna [cradle] de Nuevo León.” Both Camargo and Reynosa in turn gave birth to settlements—to Carnestolendas (1753), a ranching settlement north of the Rio Grande, and Refugio (1796), a mission-ranching node south of the Rio Grande and the precursor settlement for the town of Matamoros. From ranching settlements in Coahuila, Escandón gave authority to found and settle Revilla (1750) on the west bank of the Rio Grande, and Dolores (1750) and Laredo (1755) on the east bank. Revilla (renamed Guerrero in 1827) was a source of settlers for Zapata (1770) and later San Ygnacio (1830), "), _c('a', {
    attrs: {
      "id": "page_36"
    }
  }, [_vm._v("both east of the Rio Grande. Escandón intended to create a settlement on the San Antonio River near La Bahía, to be called Villa de Balmaceda, but resources to fund the undertaking were not forthcoming. A second trans-Nueces settlement, Villa de Vedóya, was planned with colonists from Coahuila and Nuevo León via San Juan Bautista, but the poorly supplied expedition and unsatisfactory site resulted in its being shifted to Soto la Marina in the far south of the province.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn38",
      "id": "ch3_rfn38",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("38")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("A change of viceroy in Mexico City in the mid-eighteenth century resulted in the formation of a commission to inspect the colony of Nuevo Santander, the José Tienda de Cuervo inspection described above. The 1757 survey produced an early statistical picture of Escandón’s settlements along the Rio Grande ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3tab2"
    }
  }, [_vm._v("TABLE 3.2")]), _vm._v("). Over 2,200 settlers, including missionized natives, and nearly 214,000 head of livestock were counted for six settlements and their adjoining, ill-defined ranchlands. Some settlements like Camargo and Revilla were able to cultivate corn, beans, squashes, and sugarcane, while others were dependent on trading livestock for foodstuffs.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The growth of the Rio Grande settlements toward the end of the eighteenth century led to the formation of a second commission to inspect this frontier, the general visit of the royal commission to the colonies of Nuevo Santander. This inspection was especially directed to determine boundaries for private land grants and to adjudicate land disputes outside of town limits. Because settlers were concerned about access to water for livestock in this semiarid environment, the commission decided on strict definitions for a "), _c('i', [_vm._v("porción")]), _vm._v(" or assigned land. The allotments were long quadrangles with approximately nine-thirteenths of a mile of riverfront, and the length measured from the river between 11 and 16 miles inland. The "), _c('i', [_vm._v("porciones")]), _vm._v(" created a distinctive pattern of elongated land grants along the Rio Grande between an area immediately upriver from Laredo to just downriver from Reynosa (see "), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_ch5#ch5fig2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("FIG. 5.2")]), _vm._v("), and along the lower courses of the Río Salado, Río San Juan, and Río Alamo between Revilla and Camargo."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn39",
      "id": "ch3_rfn39",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("39")])]), _vm._v(" "), _c('i', [_vm._v("Porciones")]), _vm._v(" were allocated to the town jurisdictions of Laredo, Revilla, Mier, Camargo, and Reynosa. Laredo included 88 "), _c('i', [_vm._v("porciones")]), _vm._v(" along the Rio Grande, Revilla 68 fronting the Rio Grande and Río Salado, Camargo 111 along the Rio Grande and Rio San Juan, Mier 80 fronting the Rio Alamo, and Reynosa 80 on the Rio Grande."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn40",
      "id": "ch3_rfn40",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("40")])]), _vm._v(" In addition to "), _c('i', [_vm._v("porciones")]), _vm._v(", the Spanish Crown issued large grants of land to the sons of many of the original "), _c('i', [_vm._v("porción")]), _vm._v(" grantees, including many allotments to influential families in Camargo and Reynosa. These grants were for ranching north and east of the Rio Grande in parts of what are today Hidalgo, Cameron, Willacy, Webb, and Zapata Counties, as well as the massive José Vásquez Borrego grant downriver from Laredo."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn41",
      "id": "ch3_rfn41",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("41")])]), _vm._v(" In some of these locations, especially around Laredo, Camargo and Reynosa, sheep outnumbered cattle in the first half of the eighteenth century."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn42",
      "id": "ch3_rfn42",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("42")])])]), _vm._v(" "), _c('p', {
    staticClass: "tab-cap",
    attrs: {
      "id": "ch3tab2"
    }
  }, [_c('b', [_vm._v("TABLE 3.2")]), _vm._v("   Population and Livestock in Rio Grande Settlements, 1757")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "image",
      "src": "html/images/image36_00.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "source"
  }, [_vm._v("SOURCES: Lawrence Francis Hill, "), _c('i', [_vm._v("José")]), _vm._v(" de "), _c('i', [_vm._v("Escandón and the Founding of Nuevo Santander, Estado general de las fundaciones hechas por D. José de Escandón en la colonía del Nuevo Santander")]), _vm._v("; Florence Johnson Scott. "), _c('i', [_vm._v("Historical Heritage of the Lower Rio Grande")]), _vm._v(": Oakah L. Jones, "), _c('i', [_vm._v("Los Paisanos: Spanish Settlers on the Northern Frontier of New Spain")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "sourceb"
  }, [_c('sup', [_vm._v("a")]), _vm._v("Spanish, mestizo, or mulato."), _c('br'), _c('sup', [_vm._v("b")]), _vm._v("Differentiated as horses and mules; cattle and sheep; goats."), _c('br'), _c('sup', [_vm._v("c")]), _vm._v("Given as families; 35 at Mier, 23 at Dolores, 15 at Laredo. Computed as one family equals four individuals.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_vm._v("Following Mexican independence from Spain in 1821, the settlement of Texas and Tamaulipas became priorities. Both the Mexican federal government and the states of Coahuila y Texas, as well as Tamaulipas, passed colonization laws between 1824 and 1830 to encourage settlement. In Texas, settlement was directed under the auspices of immigration agents known as "), _c('i', [_vm._v("empresarios")]), _vm._v(". However, of some twenty-four "), _c('i', [_vm._v("empresario")]), _vm._v(" contracts issued for all of Texas between 1825 and 1832, seventeen went to foreigners, mostly Anglo Americans.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn43",
      "id": "ch3_rfn43",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("43")])]), _vm._v(" Only a few of these resulted in colonies between the Nueces and Guadalupe Rivers. South of the Nueces, colonization efforts were even less fruitful. Native depredations in the Nueces strip, the perception of a wilderness environment, and an uncertain political alignment largely checked successful settlement of this zone ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In Texas, the most successful Mexican "), _c('i', [_vm._v("empresario")]), _vm._v(" was Don Martín "), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("De León, native of Burgos, Spain, and onetime permanent resident of Burgos, Tamaulipas. De León moved to San Antonio around 1800 and established a cattle ranch in the vicinity, the stock from which he drove to New Orleans and sold each year. In 1824 De León was authorized to transport forty-one colonists to a site on the lower Guadalupe River known as Cypress Grove, which he renamed Guadalupe Victoria after the president of Mexico (")]), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v("). Victoria was platted by J.M.J. Carvajal of San Antonio in the Mexican fashion, with a grid and large public plazas for municipal, ecclesiastical, and marketplace functions. The 30,000-acre Garcitas Creek ranch, situated in tall grass prairie near Matagorda Bay, was an economic mainstay of the colony, which totaled three hundred residents in 1834."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn44",
      "id": "ch3_rfn44",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("44")])])]), _vm._v(" "), _c('p', {
    staticClass: "image",
    attrs: {
      "id": "ch3fig4"
    }
  }, [_c('img', {
    staticStyle: {
      "width": "91%"
    },
    attrs: {
      "alt": "image",
      "src": "html/images/image38_00.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("3.4. Towns founded by Mexicans and Mexican Americans, 1824–1880.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("Two additional "), _c('i', [_vm._v("empresario")]), _vm._v(" colonies were planted north of the Nueces River, and each included Mexican as well as foreign settlers. In 1831 John McMullen and James McGloin, originally from Ireland and residents of Matamoros, were granted the right to settle a site known as Paso de Santa Margarita on the north bank of the Nueces.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn45",
      "id": "ch3_rfn45",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("45")])]), _vm._v(" This settlement came to be called San Patricio de Hibernia ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v("). It was laid out in the typical Mexican manner of a grid focused on plazas, and by 1834 counted some six hundred residents."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn46",
      "id": "ch3_rfn46",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("46")])]), _vm._v(" In 1834, a second Irish Mexican colony was established at the Spanish mission site of Refugio on the San Antonio River upstream from La Bahía. This site had been abandoned during the 1820s and then reoccupied by various squatters, including several Irish families, before the principal colonization party arrived."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn47",
      "id": "ch3_rfn47",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("47")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In Tamaulipas, south of the Nueces, there were several ill-fated attempts to colonize, but only three new settlements proved successful, two in present-day South Texas."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn48",
      "id": "ch3_rfn48",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("48")])]), _vm._v(" On the south bank of the Nueces and situated where the Matamoros road crossed the river, Mexican general Manuel de Mier y Terán established San Miguel Lipantitlán in 1830, a garrison intended as defense against marauding Lipan Apache and to monitor smuggling at Corpus Christi Bay ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v("). The settlement was short-lived and no permanent town resulted."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn49",
      "id": "ch3_rfn49",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("49")])]), _vm._v(" On the north bank of the Rio Grande between Laredo and Zapata, private settlers from Revilla (Guerrero) founded San Ygnacio in 1830 ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v(") under the leadership of Don Jesús Treviño, a wealthy alderman who purchased land from the heirs of the Vásquez Borregos family."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn50",
      "id": "ch3_rfn50",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("50")])]), _vm._v(" San Ygnacio survived into the era before railroads as a ranching and freighting station connecting Corpus Christi with Monclova and Saltillo (see "), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_ch6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Chap. 6")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Easily the most successful town founded in the region was Matamoros in 1823, south of the Rio Grande in Tamaulipas at the site of the late-eighteenth-century mission Congregación del Refugio and facing what would become Brownsville north of the river in 1848 ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v("). Before 1848, Matamoros was a central place for ranchers north of the Rio Grande and was visited for supplies as well as seasonally during the winter holidays. Matamoros and its Gulf port satellite of Bagdad would become a major pivot point for the Confederate cotton trade during the Civil War, and this notoriety attracted a substantial cosmopolitan population numbering perhaps fourteen thousand at its nineteenth-century apex."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn51",
      "id": "ch3_rfn51",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("51")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("While towns were the principal nodes and largest concentrations of Mexican settlement in the South Texas region, ranchos were more numerous as individual homesteads, although the success of these varied "), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("by era. For example, in the first half of the nineteenth century, ranch economies and lifestyles suffered greatly as a result of civil conflict and native raiding.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn52",
      "id": "ch3_rfn52",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("52")])]), _vm._v(" In 1834, Almonte recorded only five thousand cattle and sheep for the entire Department of Bexar, which included the settlements of San Antonio, Victoria, San Patricio, Refugio, and Goliad (La Bahía)."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn53",
      "id": "ch3_rfn53",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("53")])]), _vm._v(" After Santa Anna relinquished the Nueces strip in 1836, many Mexicans withdrew from the area to towns and ranches south of the Rio Grande. After 1848, however, ranches became reoccupied and the numbers of livestock, especially sheep and cattle, increased markedly."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn54",
      "id": "ch3_rfn54",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("54")])]), _vm._v(" By 1882, cattle were most densely concentrated in counties along the San Antonio River where grasses were abundant, while sheep came to dominate much of the trans-Nueces, a distinctively drier zone, particularly Webb, Encinal, Maverick, and Duval Counties ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig5"
    }
  }, [_vm._v("FIG. 3.5")]), _vm._v("). A Mexican boundary commission report issued in 1875 counted 157 ranchos along the north side of the Rio Grande between San Felipe del Río (Del Rio after 1869) and the mouth of the river below Brownsville."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn55",
      "id": "ch3_rfn55",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("55")])])]), _vm._v(" "), _c('p', {
    staticClass: "image",
    attrs: {
      "id": "ch3fig5"
    }
  }, [_c('img', {
    staticStyle: {
      "width": "93%"
    },
    attrs: {
      "alt": "image",
      "src": "html/images/image40_00.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("3.5. Sheep and goats versus cattle in South Texas, 1882.")]), _vm._v(" "), _c('p', {
    staticClass: "h3"
  }, [_c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("MEXICAN AMERICAN TERRITORY")])]), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }), _c('p', {
    staticClass: "noindent"
  }, [_c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("At the close of the nineteenth century, the population of South Texas was chiefly concentrated in two settled areas, which were predominantly Mexican American. These were the urban node of San Antonio (Bexar County), and a ten-county zone in deep South Texas that included Cameron, Hidalgo, Starr, Zapata, Webb, Encinal, Duval, Nueces, La Salle, and Maverick Counties.")]), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn56",
      "id": "ch3_rfn56",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("56")])]), _vm._v(" Between 1850 and 1880, the population of Mexican Americans in these counties increased from 30,040 to 43,397, and in half of these counties over 90 percent of the population was Mexican American."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn57",
      "id": "ch3_rfn57",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("57")])]), _vm._v(" By comparison, the Mexican American population of San Antonio was 1,642 in 1850, some 46 percent of the city’s population, but that percentage dropped to 41 percent in 1880, although the number of Mexican Americans grew to 8,425."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn58",
      "id": "ch3_rfn58",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("58")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("These relative changes in population and the proportion of Mexican Americans suggest something of the geographic shift that occurred in the second half of the nineteenth century in the region. Mexican-origin populations continued to migrate to counties along the Rio Grande from Mexico, as well to interior counties of the region, but there was beginning to emerge a consequent Anglicizing of counties along the northern edge of South Texas, especially north of the Nueces River ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig6"
    }
  }, [_vm._v("FIG. 3.6")]), _vm._v("). To the north and west of the river were Zavala, Frio, Medina, and McMullen counties, where the principal immigrant groups were southern Anglo Americans from Missouri, Arkansas, and Louisiana. Counties north and east of the river like Wilson, Karnes, Goliad, Victoria and part of Bee were being populated by immigrants from Alabama, Louisiana, and Arkansas.")]), _vm._v(" "), _c('p', {
    staticClass: "indentb"
  }, [_vm._v("Even in counties that persisted as predominantly Mexican American, Anglo American immigrants were significant in organizing new towns. Corpus Christi was founded as a trading center in 1839, before the Mexican War, and many of the river towns such as Rio Grande City, Roma, Brownsville, Eagle Pass, Hidalgo, and Del Rio date from near the end of the war or shortly thereafter ("), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v("). All except Roma, Hidalgo, and Del Rio were first organized as federal forts."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn59",
      "id": "ch3_rfn59",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("59")])]), _vm._v(" Anglo Americans like Charles Stillman, Henry Clay Davis, and John Twohig were instrumental in founding these settlements, yet the towns remained chiefly Mexican American places where Spanish language and Mexican cultural ways and environments prevailed. John G. Bourke, certainly one of the region’s most pointed observers, remarked in 1894 that the lower valley along the Rio Grande was terra incognita to the rest of the United States:")]), _vm._v(" "), _c('p', {
    staticClass: "extract1"
  }, [_vm._v("Twice the waves of North American aggression have swept across this region, bearing down all in their path; but as the tempest abated the Mexican population placidly resumed its control of affairs and returned to its former habits of life, as if the North American had never existed."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn60",
      "id": "ch3_rfn60",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("60")])])]), _vm._v(" "), _c('p', {
    staticClass: "image",
    attrs: {
      "id": "ch3fig6"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_c('img', {
    staticStyle: {
      "width": "93%"
    },
    attrs: {
      "alt": "image",
      "src": "html/images/image42_00.jpg"
    }
  })])]), _c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_c('p', {
    staticClass: "caption"
  }, [_vm._v("3.6. Sources of migrants to South Texas counties, 1865–1885.")])]), _c('p', {
    staticClass: "indentt"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_vm._v("Interior settlements founded later, like San Diego and Benavides, established in 1852 and 1880 respectively, remained effectively Mexican American communities with small Anglo American and European minorities (")]), _c('a', {
    attrs: {
      "href": "#/html/#ch3fig4"
    }
  }, [_vm._v("FIG. 3.4")]), _vm._v(")."), _c('span', {
    attrs: {
      "href": "#/html/9780292705111_notes#ch3_fn61",
      "id": "ch3_rfn61",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("61")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In the course of nearly two centuries, and especially since the middle of the eighteenth century, South Texas took shape as a Hispanic cultural region. The evolving course of this region over the most recent century will be charted in the next chapter.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-6a0801f2","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/html/9780292705111_ch3.vue
// module id = 96
// module chunks = 0